import { useEffect, useState } from "react";

import { genteParaCompartir } from "../services/espacio";

/* El panel "Compartir con", común a planillas y documentos.
 *
 * El dueño elige compañeros. CEPI va fijo arriba, tildado y bloqueado: no es
 * un destino más de la lista —ya leemos todo por el servidor—, es el aviso
 * honesto de que lo que subís nos alimenta, con el disclaimer al pasar el
 * mouse. `onGuardar(mails, editores)` guarda en la tabla que corresponda
 * (planilla o documento), así el panel no sabe de tablas.
 *
 * Con `editores` (FiboSheets #6, sólo planillas): al lado de cada elegido,
 * «puede ver» o «puede editar». Sin esa prop (los documentos) el panel es el
 * de siempre: los que elijas LEEN, y `onGuardar` recibe sólo los mails.
 * Al elegir a alguien entra como «puede ver»: dar edición es un paso más, a
 * conciencia. Sacarlo de la lista le saca también la edición.
 *
 * Con `link`: el botón «Copiar link», para mandarle la planilla directo a
 * quien ya la tiene compartida (al resto el link le dice que no puede).
 */

const CEPI_MAIL = "cepi@fibot.ar";
export default function CompartirPanel({ compartidaCon, yo, onGuardar, que = "la planilla", editores, link }) {
  const conRoles = Array.isArray(editores);
  const [gente, setGente] = useState(null);
  const [sel, setSel] = useState(() => new Set(compartidaCon || []));
  const [eds, setEds] = useState(() => new Set(editores || []));
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState("");
  const [estado, setEstado] = useState("");
  const [copiado, setCopiado] = useState(""); // "" | "ok" | "manual"

  useEffect(() => {
    if (abierto && gente === null) {
      genteParaCompartir().then(setGente).catch(() => setGente([]));
    }
  }, [abierto, gente]);

  const guardar = (s, e) => {
    // Los editores viajan sólo si hay o hubo: sin editores ni antes ni
    // ahora, se guarda como siempre, sólo con quién está compartida.
    const conEditores = conRoles && (e.size > 0 || eds.size > 0);
    setSel(s);
    setEds(e);
    setEstado("guardando");
    (conEditores ? onGuardar([...s], [...e]) : onGuardar([...s]))
      .then(() => setEstado("guardado"))
      .catch(() => setEstado("error"));
  };
  const toggle = (mail) => {
    const s = new Set(sel);
    const e = new Set(eds);
    if (s.has(mail)) { s.delete(mail); e.delete(mail); } else s.add(mail);
    guardar(s, e);
  };
  const ponerRol = (mail, rol) => {
    const e = new Set(eds);
    if (rol === "editar") e.add(mail); else e.delete(mail);
    guardar(sel, e);
  };

  /* Al portapapeles; si el navegador no deja (http, permisos), el link queda
     a la vista y seleccionado para copiarlo a mano. */
  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado("ok");
    } catch {
      setCopiado("manual");
    }
  };

  const q = busca.trim().toLowerCase();
  const filtrada = (gente || []).filter(
    (p) => p.mail !== yo && p.mail !== CEPI_MAIL &&
      (!q || (p.nombre || "").toLowerCase().includes(q) || p.mail.includes(q)),
  );

  return (
    <div className="esp-compartir">
      <button className="esp-btn" onClick={() => setAbierto((a) => !a)}>
        🔗 Compartir con{sel.size ? ` · ${sel.size}` : ""} {abierto ? "▲" : "▼"}
      </button>
      {abierto && (
        <div className="esp-compartir-panel">
          {link && (
            <div className="esp-persona">
              <button className="esp-btn chico" onClick={copiarLink}>🔗 Copiar link</button>
              {copiado === "ok" && <span className="tenue" role="status">Link copiado ✓</span>}
              {copiado === "manual" && (
                <input
                  className="esp-buscar esp-link" readOnly value={link} aria-label="Link de la planilla"
                  onFocus={(e) => e.target.select()} autoFocus
                />
              )}
            </div>
          )}

          {/* CEPI: fijo, tildado y bloqueado. Sin globo que lo explique: en el
              hub no van textos explicativos. */}
          <label className="esp-persona esp-cepi">
            <input type="checkbox" checked readOnly disabled />
            <span>
              <b>CEPI</b> <span className="tenue">— nosotros, siempre</span>
            </span>
          </label>

          <input
            className="esp-buscar" value={busca} placeholder="Buscar un compañero…"
            aria-label="Buscar compañero" onChange={(e) => setBusca(e.target.value)}
          />

          <div className="esp-personas">
            {gente === null ? (
              <p className="tenue" style={{ padding: 8 }}>cargando…</p>
            ) : filtrada.length === 0 ? (
              <p className="tenue" style={{ padding: 8 }}>No hay compañeros para mostrar.</p>
            ) : (
              filtrada.map((p) => (
                <div key={p.mail} className="esp-persona">
                  <label className="esp-persona-nombre">
                    <input
                      type="checkbox" checked={sel.has(p.mail)}
                      onChange={() => toggle(p.mail)}
                    />
                    <span>{p.nombre} <span className="tenue">{p.mail}</span></span>
                  </label>
                  {conRoles && sel.has(p.mail) && (
                    <select
                      className="esp-btn chico" aria-label={`Permiso de ${p.nombre || p.mail}`}
                      value={eds.has(p.mail) ? "editar" : "ver"}
                      onChange={(e) => ponerRol(p.mail, e.target.value)}
                    >
                      <option value="ver">Solo lectura</option>
                      <option value="editar">Editor</option>
                    </select>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="esp-guardado" style={{ padding: "4px 8px" }}>
            {estado === "guardando" ? "guardando…" : estado === "error" ? "no se pudo guardar" : estado ? "guardado ✓"
              : conRoles
                ? `Con «Solo lectura» pueden ver ${que}; con «Editor», también cambiarla. Compartirla y borrarla es sólo tuyo.`
                : `Los que elijas pueden LEER ${que}.`}
          </div>
        </div>
      )}
    </div>
  );
}
